import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Landmark, ExternalLink, Sparkles, ShieldCheck } from "lucide-react";

export default function CatalogTab() {
  const [offers, setOffers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [ref, setRef] = useState({ offer_categories: [], financing_types: [] });
  const [bank, setBank] = useState("all");
  const [cat, setCat] = useState("all");

  const catLabel = (v) => ref.offer_categories.find((c) => c.value === v)?.label || v;

  const load = () => {
    const params = {};
    if (bank !== "all") params.bank = bank;
    if (cat !== "all") params.category = cat;
    api.get("/green-finance/offers", { params }).then((r) => { setOffers(r.data.offers); setBanks(r.data.banks); });
  };
  useEffect(() => { api.get("/green-finance/reference").then((r) => setRef(r.data)); }, []);
  useEffect(() => { load(); }, [bank, cat]);

  return (
    <div className="space-y-4" data-testid="gf-catalog-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-sm text-muted-foreground flex-1">Bank green-finance offers available to your tenant. Indicative rates are shown only where published — we never invent rates.</div>
        <Select value={bank} onValueChange={setBank}>
          <SelectTrigger className="h-8 w-40" data-testid="catalog-bank-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All banks</SelectItem>{banks.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="h-8 w-52" data-testid="catalog-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{ref.offer_categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {offers.map((o) => (
          <div key={o.id} className="card-flat p-4" data-testid={`offer-card-${o.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold text-sm inline-flex items-center gap-1.5"><Landmark className="h-4 w-4 text-accent" /> {o.bank_name} · {o.offer_name}</h4>
                  <Badge variant="outline" className="text-[9px]">{catLabel(o.offer_category)}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">{o.eligible_use_of_proceeds}</p>
              </div>
              {o.ai_extracted && <Badge variant="outline" className="text-[8px] text-[#FFB800] border-[#FFB800]/40 shrink-0"><Sparkles className="h-2.5 w-2.5 mr-0.5" />AI</Badge>}
            </div>
            {o.key_requirements && <div className="text-[11px] text-muted-foreground mt-2"><span className="text-foreground/80">Key requirements:</span> {o.key_requirements}</div>}
            <div className="flex flex-wrap gap-1 mt-2">
              {(o.sector_focus || []).map((s) => <Badge key={s} variant="outline" className="text-[8px] py-0">{s}</Badge>)}
            </div>
            <div className="flex items-center justify-between mt-3 text-[11px] text-muted-foreground">
              <span>{o.indicative_interest_rate != null ? `Rate ~${o.indicative_interest_rate}%` : "Rate not published"}{o.indicative_tenure ? ` · ${o.indicative_tenure}` : ""}</span>
              <a href={o.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline" data-testid={`offer-source-${o.id}`}>Source <ExternalLink className="h-3 w-3" /></a>
            </div>
            <div className="text-[9px] text-muted-foreground mt-1 inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Verified {o.verified_date ? new Date(o.verified_date).toLocaleDateString() : "—"} · v{o.version}</div>
          </div>
        ))}
        {!offers.length && <div className="text-sm text-muted-foreground py-6">No offers in the catalog for this filter.</div>}
      </div>
    </div>
  );
}
